import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FLIGHT_ENDPOINTS, USER_ENDPOINTS } from '../config/api';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Tooltip,
  IconButton
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

const FlightForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  dayjs.extend(utc)
  
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [webhooks, setWebhooks] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    airline: '',
    origin: '',
    destination: '',
    type: 'one-way',
    outboundDeparture: dayjs.utc(),
    outboundArrival: dayjs.utc(),
    inboundDeparture: null,
    inboundArrival: null,
    numberOfPeople: 1,
    minimumPrice: 0,
    webhookId: '',
  });

  useEffect(() => {
    if (isEditMode) {
      fetchFlightDetails();
    }
    fetchWebhooks();
  }, [id]);

  const fetchWebhooks = async () => {
    try {
      const response = await fetch(USER_ENDPOINTS.WEBHOOKS, {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch webhooks');
      }
    } catch (err) {
      setError('Something went wrong while fetching webhooks');
    }
  };

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(FLIGHT_ENDPOINTS.DETAIL(id), {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const flight = await response.json();
        setFormData({
          ...flight,
          outboundDeparture: flight.outboundDeparture ? dayjs.utc(flight.outboundDeparture) : dayjs.utc(),
          outboundArrival: flight.outboundArrival ? dayjs.utc(flight.outboundArrival) : dayjs.utc(),
          inboundDeparture: flight.inboundDeparture ? dayjs.utc(flight.inboundDeparture) : null,
          inboundArrival: flight.inboundArrival ? dayjs.utc(flight.inboundArrival) : null,
        });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch flight details');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange = (name, date) => {
    setFormData({
      ...formData,
      [name]: date,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const endpoint = isEditMode 
        ? FLIGHT_ENDPOINTS.DETAIL(id) 
        : FLIGHT_ENDPOINTS.LIST;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const submissionData = {
        ...formData,
        outboundDeparture: formData.outboundDeparture ? formData.outboundDeparture.toISOString() : null,
        outboundArrival: formData.outboundArrival ? formData.outboundArrival.toISOString() : null,
        inboundDeparture: formData.inboundDeparture ? formData.inboundDeparture.toISOString() : null,
        inboundArrival: formData.inboundArrival ? formData.inboundArrival.toISOString() : null,
      };
      
      const response = await fetch(endpoint, {
        method,
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (response.ok) {
        setSuccess(isEditMode ? 'Flight updated successfully!' : 'Flight added successfully!');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to ${isEditMode ? 'update' : 'add'} flight`);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Flight' : 'Add New Flight'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* Basic Information Section */}
        <Card sx={{ mb: 4, boxShadow: 2 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
              Tracker Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="name"
                  label="Tracker Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="number"
                  id="trackingFrequency"
                  label="Tracking Frequency (Min.)"
                  name="trackingFrequency"
                  value={formData.trackingFrequency}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 9 }}
                  sx={{minWidth: 200}}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel id="webhook-label">Webhook</InputLabel>
                  <Select
                    labelId="webhook-label"
                    id="webhookId"
                    name="webhookId"
                    value={formData.webhookId}
                    label="Webhook"
                    onChange={handleChange}
                    sx={{minWidth: 200}}
                  >
                    {webhooks.map((webhook) => (
                      <MenuItem key={webhook.id} value={webhook.id}>
                        {webhook.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Route Information Section */}
        <Card sx={{ mb: 4, boxShadow: 2 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
              Route Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="airline"
                  name="airline"
                  value={formData.airline}
                  onChange={handleChange}
                  helperText="2-3 letter IATA code"
                  slotProps={{inputLabel: {
                    sx: {
                      display: 'flex',
                      alignItems: 'center',
                      whiteSpace: 'nowrap', // 🧙‍♂️ This prevents the * from wrapping
                      '& .MuiInputLabel-asterisk': {
                        marginLeft: '2px',
                      }
                    }
                  }}}
                  label={
                    <Box display="flex" alignItems="center">
                      Airline (IATA Code)
                      <Tooltip 
                        title={
                          <Box>
                            <div>TAM - LATAM</div>
                            <div>TAP - TAP Portugal</div>
                            <div>RUK - Ryanair</div>
                            <div>EZY - easyJet</div>
                            <div>IBE - Iberia</div>
                            {/* Add more airlines here if you like */}
                          </Box>
                        }
                      >
                        <IconButton size="small" sx={{ ml: 1 }}>
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="origin"
                  label="Origin (IATA Code)"
                  name="origin"
                  value={formData.origin}
                  onChange={handleChange}
                  helperText="3 letter IATA code"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="destination"
                  label="Destination (IATA Code)"
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  helperText="3 letter IATA code"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Flight Details Section */}
        <Card sx={{ mb: 4, boxShadow: 2 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
              Flight Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel id="type-label">Flight Type</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    value={formData.type}
                    label="Flight Type"
                    onChange={handleChange}
                    sx={{minWidth: 160}}
                  >
                    <MenuItem value="one-way">One Way</MenuItem>
                    <MenuItem value="return">Return</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="number"
                  id="numberOfPeople"
                  label="Number of People"
                  name="numberOfPeople"
                  value={formData.numberOfPeople}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 9 }}
                  sx={{minWidth: 160}}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="number"
                  id="minimumPrice"
                  label="Minimum Price"
                  name="minimumPrice"
                  value={formData.minimumPrice}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              
            </Grid>
          </CardContent>
        </Card>

        {/* Schedule Section */}
        <Card sx={{ mb: 4, boxShadow: 2 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
              Schedule
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Outbound Departure"
                    value={formData.outboundDeparture}
                    onChange={(newValue) => handleDateChange('outboundDeparture', newValue)}
                    ampm={false}
                    format="DD/MM/YYYY, HH:mm"
                    timezone='UTC'
                    slotProps={{ 
                      textField: { 
                        fullWidth: true, 
                        required: true
                      } 
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Outbound Arrival"
                    value={formData.outboundArrival}
                    onChange={(newValue) => handleDateChange('outboundArrival', newValue)}
                    ampm={false}
                    format="DD/MM/YYYY, HH:mm"
                    timezone='UTC'
                    slotProps={{ 
                      textField: { 
                        fullWidth: true, 
                        required: true
                      } 
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {formData.type === 'return' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        label="Inbound Departure"
                        value={formData.inboundDeparture}
                        onChange={(newValue) => handleDateChange('inboundDeparture', newValue)}
                        ampm={false}
                        format="DD/MM/YYYY, HH:mm"
                        timezone='UTC'
                        slotProps={{ 
                          textField: { 
                            fullWidth: true, 
                            required: true
                          } 
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        label="Inbound Arrival"
                        value={formData.inboundArrival}
                        onChange={(newValue) => handleDateChange('inboundArrival', newValue)}
                        ampm={false}
                        format="DD/MM/YYYY, HH:mm"
                        timezone='UTC'
                        slotProps={{ 
                          textField: { 
                            fullWidth: true, 
                            required: true
                          } 
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2,
          mt: 2,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={submitting}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            fullWidth={isMobile}
          >
            {submitting ? 'Saving...' : isEditMode ? 'Update Flight' : 'Add Flight'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default FlightForm; 